import { useState, useEffect } from "react";
import { useAuth } from "src/auth/AuthContext";
import { useSnackbar } from "src/context/ShowMessage";

interface ApiResponse<T> {
  status: number;
  data: T;
}

const useFetch = <T>(
  fetchFunction: (signal?: AbortSignal) => Promise<ApiResponse<T>> | null,
  dependencies: any[],
  fetchCondition?: boolean,
  callBack?: (data: any | null) => void,
  initialFn?: () => void,
  effectMount?: () => void,
  suppressErrorMessage = false,
) => {
  const { displayMessage } = useSnackbar();
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const auth = useAuth();

  useEffect(() => {
    initialFn?.();
    const ctrl = new AbortController(); // Create a new AbortController
    const signal = ctrl.signal;

    if (!fetchCondition) {
      setLoading(false);
      return;
    }
    let isFetch = true;

    const fetchData = async () => {
      try {
        setLoading(true);
        const res: ApiResponse<any> | null = await fetchFunction(signal);

        if (res && res.status === 200) {
          if (Array.isArray(res.data) && isFetch) {
            setData(res.data || []);
            callBack && callBack(res.data);
          } else if (isFetch) {
            setData(res.data.records || res.data);

            setTotalCount(res.data?.totalRecords || 0);
            callBack && callBack(res.data.records || res.data);
          }
        } else if (res && res.status === 404) {
          callBack && callBack([]);
          setData([]);
        } else if (res && res.status === 401) {
          auth.signOut();
        } else if (!suppressErrorMessage) {
          displayMessage("Failed to fetch data", "error");
        }
      } catch (err: any) {
        if (err.name === "AbortError" || signal.aborted) {
          console.log("Fetch aborted");
        } else if (!suppressErrorMessage) {
          displayMessage(err.message || "An error occurred", "error");
        }
      } finally {
        if (isFetch) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      ctrl.abort();
      isFetch = false;
      effectMount?.();
    };
  }, dependencies);

  return [data, loading, totalCount];
};

export default useFetch;
